/**
 * The call's "line" — which line/department it came in on — as one display
 * label, derived from the columns the processor stamps (see
 * processor.server.ts#applyLineIdentity for the route rule):
 *
 *   queue    → "Customer Service" (queue name; number as fallback)
 *   direct   → "Direct · Jill Smith" (extension owner's name, else the dn)
 *   internal → "Internal · Jill Smith"
 *   outbound → "Outbound · Jill Smith"
 *   unknown  → legacy rows fall back to whatever queue name was recorded;
 *              when nothing is known the label is null — never fabricated.
 *
 * When 3CX gives us the dialed DID it is appended ("Customer Service ·
 * (865) 555-0100"); the real v20 frames usually don't carry it (see the DID
 * note in the processor), so most labels are name-only.
 *
 * Pure and client-safe on purpose: the server payloads carry the computed
 * label, and unit tests hit this directly.
 */

/**
 * A call 3CX sent straight to a person's voicemail (they were Busy, Away or on
 * DND) produces NO call-control events, so the watcher never sees it. The call
 * reconciliation pass records it from Telnyx's detail record instead (see
 * voicemail-miss.server.ts), under a `cx_call_id` of this prefix plus Telnyx's
 * own call id — the same idea as the processor's `pid:` fallback for a leg
 * with no 3CX call id. The prefix is also THE marker: a row carrying it went
 * to voicemail without the phone ringing.
 */
export const VOICEMAIL_CX_PREFIX = 'telnyx-voicemail:'

/**
 * calls.calls.line_kind for the "Ring my mobile" leg: 3CX dialling the
 * extension's own simultaneous-ring mobile beside an inbound direct call
 * (processor.server.ts#skipMobileIntake). Never a customer call — the lists
 * leave it out, the missed-call rule never sees it (issue #187).
 *
 * Defined HERE, in a module with no imports, on purpose: processor.server.ts →
 * work/service.server.ts → calls/service.server.ts is an import cycle, and
 * calls/service reads this at module top level. Exported from the processor it
 * was a ReferenceError whenever the watcher loaded first (own-mobile-import-
 * order.test.ts).
 */
export const OWN_MOBILE = 'own_mobile'

/** The row id a straight-to-voicemail call is recorded under — one per Telnyx call. */
export function voicemailCxCallId(telnyxId: string): string {
	return `${VOICEMAIL_CX_PREFIX}${telnyxId}`
}

/** Did this call go straight to voicemail, without the phone ringing? */
export function isVoicemailOnlyCall(
	cxCallId: string | null | undefined,
): boolean {
	return Boolean(cxCallId?.startsWith(VOICEMAIL_CX_PREFIX))
}

/**
 * An inbound call nobody answered that the app NEVER SAW — no 3CX frame, no
 * row — recorded by the reconciliation pass from Telnyx's detail record
 * (missed-recovery.server.ts), under a `cx_call_id` of this prefix plus
 * Telnyx's own call id. Like a straight-to-voicemail row, it stands for
 * exactly that one Telnyx call and nobody was ever on it.
 */
export const RECOVERED_CX_PREFIX = 'telnyx-missed:'

/** The row id a recovered missed call is recorded under — one per Telnyx call. */
export function recoveredCxCallId(telnyxId: string): string {
	return `${RECOVERED_CX_PREFIX}${telnyxId}`
}

/**
 * The Telnyx call a row the reconciliation pass wrote from the carrier's
 * record stands for (straight to voicemail, or a recovered miss), or null for
 * a row the watcher wrote.
 */
export function carrierRecordedTelnyxId(
	cxCallId: string | null | undefined,
): string | null {
	if (!cxCallId) return null
	for (const prefix of [VOICEMAIL_CX_PREFIX, RECOVERED_CX_PREFIX]) {
		if (cxCallId.startsWith(prefix)) return cxCallId.slice(prefix.length)
	}
	return null
}

/** "(615) 555-0123" for NANP numbers; anything else unchanged. */
export function formatPhone(raw: string | null | undefined): string {
	if (!raw) return 'Unknown number'
	const d = raw.replace(/\D/g, '')
	if (d.length === 11 && d.startsWith('1'))
		return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`
	if (d.length === 10)
		return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
	return raw
}

/** The slice of a calls.calls row the label needs. */
export type CallLineInfo = {
	line_kind: string | null
	line_dn: string | null
	queue_number: string | null
	queue_name: string | null
	did?: string | null
}

/**
 * One label for the line a call came in on, or null when nothing is known.
 * `names` maps extension → display name (calls.extension_users); a dn with no
 * name shows as "ext <dn>".
 */
export function lineLabel(
	call: CallLineInfo,
	names: Record<string, string> = {},
): string | null {
	const did = call.did ? ` · ${formatPhone(call.did)}` : ''
	const queueName =
		call.queue_name ?? (call.queue_number ? `Queue ${call.queue_number}` : null)
	const who = call.line_dn
		? names[call.line_dn] || `ext ${call.line_dn}`
		: null
	switch (call.line_kind) {
		case 'queue':
			return queueName ? `${queueName}${did}` : null
		case 'direct':
			return `Direct · ${who ?? 'unknown extension'}${did}`
		case 'internal':
			return who ? `Internal · ${who}` : 'Internal'
		case 'outbound':
			return who ? `Outbound · ${who}` : 'Outbound'
		case 'own_mobile':
			// 3CX ringing the person's own mobile beside an inbound direct call
			// (processor#skipMobileIntake) — named for what it is, never "Outbound"
			return who ? `Own mobile · ${who}` : 'Own mobile'
		default:
			// legacy rows (before line_kind existed): show the queue stamp when
			// there is one, nothing otherwise — no fake labels
			return queueName ? `${queueName}${did}` : null
	}
}
